import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "@/design-system/native";
import { Edit, Menu, Search as SearchIcon, X, XCircle, Bookmark, CheckCircle, ChevronDown, Columns2, Filter, Star } from "lucide-react";

import styles from "./ListTable.module.scss";

import { DatePicker } from "@/design-system/components/DateTimeField";
import { Link } from "react-router-dom";
import DataTable from "../../Tables/DataTable/DataTable";
import TableCard from "../../Tables/DataTable/TableCard";
import Pager from "../../Tables/DataTable/Pagination";
import FilterModal from "./FilterModal";
import ManageFiltersModal from "./ManageFiltersModal";
import { LabelChip, PointsPill, PriorityBadge } from "../../Common/EntityWorkspace";
import ExportButtons from "../../Common/ExportButtons";
import { StatusPill } from "../../../design-system";
import defaultAvatar from "../../../assets/images/user/user.png";
import { profiles } from "../../../_helper/User/UserData/data";

// ---------------- Helpers ----------------
const STATUS_OPTIONS = [
  "To Do",
  "In progress",
  "Admin Approval",
  "Customer Approval",
  "Done",
];

const STATUS_COLORS = {
  "To Do": {
    fg: "#38a4f8",
    bg: "rgb(56 164 248 / 10%)",
    br: "rgb(56 164 248 / 24%)",
  },
  "In progress": {
    fg: "#ffb829",
    bg: "rgb(255 184 41 / 10%)",
    br: "rgb(255 184 41 / 24%)",
  },
  "Admin Approval": {
    fg: "#f5325c",
    bg: "rgb(245 50 92 / 10%)",
    br: "rgb(245 50 92 / 24%)",
  },
  "Customer Approval": {
    fg: "#2c587e",
    bg: "rgb(44 88 126 / 10%)",
    br: "rgb(44 88 126 / 24%)",
  },
  Done: {
    fg: "#0abb87",
    bg: "rgb(10 187 135 / 10%)",
    br: "rgb(10 187 135 / 24%)",
  },
};

const getStatusColors = (status) =>
  STATUS_COLORS[status] || {
    fg: "#374151",
    bg: "rgb(243 244 246 / 100%)",
    br: "rgb(229 231 235 / 100%)",
  };

const createOptions = (tasks, key) =>
  Array.from(new Set(tasks.map((t) => t[key]).filter(Boolean))).map((x) => ({
    label: x,
    value: x,
  }));

const toDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val;
  const s = String(val).trim();
  const m1 = s.match(
    /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
  );
  if (m1)
    return new Date(
      Number(m1[3]),
      Number(m1[2]) - 1,
      Number(m1[1]),
      m1[4] || 0,
      m1[5] || 0
    );
  const m2 = s.match(
    /^(\d{4})[./-](\d{1,2})[./-](\d{1,2})(?:\s+(\d{1,2}):(\d{2}))?$/
  );
  if (m2)
    return new Date(
      Number(m2[1]),
      Number(m2[2]) - 1,
      Number(m2[3]),
      m2[4] || 0,
      m2[5] || 0
    );
  const d = new Date(s);
  return isNaN(d) ? null : d;
};
const ICONS = {
  bookmark: Bookmark,
  star: Star,
  filter: Filter,
};

const TASK_USER_DIRECTORY = profiles
  .filter((profile) => profile?.type === "user" && profile?.overview?.name)
  .map((profile) => ({
    id: String(profile.id),
    name: profile.overview.name,
    avatar: profile.overview.avatar || defaultAvatar,
  }));

const splitPeopleValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (!value) return [];

  return String(value)
    .split(/[,;\n|]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const resolveTaskUsers = (value, idValue) => {
  const ids = splitPeopleValue(idValue);
  if (ids.length) {
    return ids
      .map((id) =>
        TASK_USER_DIRECTORY.find((user) => String(user.id) === String(id))
      )
      .filter(Boolean);
  }

  const names = splitPeopleValue(value);
  return names
    .map((name) =>
      TASK_USER_DIRECTORY.find((user) => user.name === String(name))
    )
    .filter(Boolean);
};

const AvatarStack = ({ users = [] }) => {
  const visibleUsers = (users || []).slice(0, 4);

  if (!visibleUsers.length) {
    return <span className={styles.muted}>-</span>;
  }

  return (
    <div className={styles.avatarStack}>
      {visibleUsers.map((user, index) => (
        <img
          key={`${user.id}-${index}`}
          src={user.avatar || defaultAvatar}
          alt={user.name}
          title={user.name}
          width={28}
          height={28}
          className={styles.avatar}
        />
      ))}
    </div>
  );
};

// ---------------- Status Cell ----------------
// design-system StatusPill primitive'i kullanıyoruz; renk paleti
// STATUS_COLORS → { bg, fg, border } şeklinde colorMap'e çevrilir.
const STATUS_COLOR_MAP = Object.fromEntries(
  Object.entries(STATUS_COLORS).map(([k, v]) => [
    k,
    { bg: v.bg, fg: v.fg, border: v.br },
  ])
);

const StatusCell = ({ task, setTasks }) => {
  if (!task) return null;
  const apply = (val) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, status: val } : t))
    );
  };
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <StatusPill
        value={task.status}
        options={STATUS_OPTIONS}
        colorMap={STATUS_COLOR_MAP}
        onChange={apply}
        size="xs"
      />
    </span>
  );
};

// ---------------- Main ----------------
export default function ListTable({
  rows,
  setRows,
  onOpenInfo,
  onEdit,
  onDelete,
  labelColors = {},
  columns: columnsProp,
  hiddenColumns = [],
  features = {},
  pageSize = 10,
  title = "Tasks",
  setSelectedTask,
  setEditModalOpen,
  setTaskToDelete,
  setDeleteModalOpen,
  activeTaskId = null,
  inspectorApi = null,
}) {
  const featureFlags = {
    statusSearch: true,
    savedFilters: true,
    filterBar: true,
    filterModal: true,
    manageFilters: true,
    columnMenu: true,
    exportButtons: true,
    ...features,
  };

  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(new Set(hiddenColumns));
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    project: "",
    milestone: "",
    priority: "",
    label: "",
    relatedTo: "",
    assignedTo: "",
    startDate: null,
    deadline: null,
  });
  const [page, setPage] = useState(1);
  const [pageSizeState, setPageSizeState] = useState(pageSize);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [activeSavedFilter, setActiveSavedFilter] = useState(null);
  const [quickFilterOpen, setQuickFilterOpen] = useState(false);
  const [manageFiltersOpen, setManageFiltersOpen] = useState(false);
  const [editFilterData, setEditFilterData] = useState(null);
  const [filterMode, setFilterMode] = useState("add"); // "add" | "

  // hiddenColumns prop (parent default `[]`) her render YENİ array ref'i →
  // bu effect her render fire ediyordu; `new Set(...)` koşulsuz yeni ref
  // üretince setHiddenCols her seferinde re-render tetikliyor → "Maximum update
  // depth" sonsuz döngü (§11). KÖK çözüm: içerik değişmediyse prev'i döndür →
  // React setState'i bail eder (referans aynı), döngü kırılır.
  useEffect(() => {
    setHiddenCols((prev) => {
      const next = new Set(hiddenColumns);
      if (prev.size === next.size && [...prev].every((c) => next.has(c))) {
        return prev;
      }
      return next;
    });
  }, [hiddenColumns]);

  const openFilterInspector = (nextMode = "add", initialData = null) => {
    inspectorApi?.openInspector({
      mode: "filters",
      payload: {
        filterMode: nextMode,
        initialData,
        icons: ICONS,
        onSave: handleSaveFilter,
      },
    });
  };

  const openManageFiltersInspector = () => {
    inspectorApi?.openInspector({
      mode: "manage-filters",
      payload: {
        filters: savedFilters,
        onApply: applySavedFilter,
        onEdit: (filter) => editFilter(filter),
        onDelete: deleteFilter,
      },
    });
  };

  const toggleFilterModal = () => {
    if (!featureFlags.filterModal) return;
    if (inspectorApi) {
      openFilterInspector(filterMode, editFilterData);
      return;
    }
    setFilterModalOpen((v) => !v);
  };

  // Kaydet (Add + Edit için ortak)
  const handleSaveFilter = (newFilter, mode = "add") => {
    if (mode === "edit") {
      setSavedFilters((prev) =>
        prev.map((f) => (f.id === newFilter.id ? { ...newFilter } : f))
      );
    } else {
      const snapshot = { ...filters };
      setSavedFilters((prev) => [...prev, { ...newFilter, snapshot }]);
    }
  };

  // Uygula
  const applySavedFilter = (filter) => {
    setFilters(filter.snapshot);
    setActiveSavedFilter(filter.id);
    setPage(1);
  };

  // Sil
  const deleteFilter = (id) => {
    setSavedFilters((prev) => prev.filter((f) => f.id !== id));
  };

  // Düzenle
  const editFilter = (filter) => {
    setEditFilterData(filter);
    setFilterMode("edit");
    if (inspectorApi) {
      openFilterInspector("edit", filter);
      return;
    }
    setFilterModalOpen(true);
  };

  // ✅ Tıklanabilir chip/priority/points için ek filtre durumu
  const [labelFilter, setLabelFilter] = useState(null);
  const toggleFilter = (type, value) => {
    setPage(1);
    setLabelFilter(
      labelFilter && labelFilter.type === type && labelFilter.value === value
        ? null
        : { type, value }
    );
  };

  const useTasks = rows;
  const updateTasks = setRows;

  // ---- filter options ----
  const labelFilterOptions = useMemo(() => {
    const labels = useTasks.flatMap((t) =>
      Array.isArray(t.labels)
        ? t.labels
        : t.labels
        ? String(t.labels)
            .split(",")
            .map((s) => s.trim())
        : []
    );
    return Array.from(new Set(labels.filter(Boolean))).map((x) => ({
      label: x,
      value: x,
    }));
  }, [useTasks]);

  const filterConfigs = [
    { key: "status", label: "- Status -" },
    { key: "project", label: "- Project -" },
    { key: "milestone", label: "- Milestone -" },
    { key: "priority", label: "- Priority -" },
    { key: "label", label: "- Label -", custom: labelFilterOptions },
    { key: "assignedTo", label: "- Assigned to -" },
    { key: "relatedTo", label: "- Related to -" },
  ];

  // ---- filtering ----
  const filtered = useMemo(() => {
    return useTasks.filter((t) => {
      const q = search.trim().toLowerCase();
      const labelsText = Array.isArray(t.labels)
        ? t.labels.join(" ")
        : t.labels || "";

      const baseChecks = [
        !q ||
          String(t.id).includes(q) ||
          (t.title || "").toLowerCase().includes(q) ||
          (t.relatedTo || "").toLowerCase().includes(q) ||
          (t.assignedTo || "").toLowerCase().includes(q) ||
          (t.status || "").toLowerCase().includes(q) ||
          labelsText.toLowerCase().includes(q),
        !filters.status || t.status === filters.status,
        !filters.project || t.project === filters.project,
        !filters.milestone || t.milestone === filters.milestone,
        !filters.priority || t.priority === filters.priority,
        !filters.relatedTo || t.relatedTo === filters.relatedTo,
        !filters.label ||
          (Array.isArray(t.labels)
            ? t.labels.includes(filters.label)
            : (t.labels || "").includes(filters.label)),
        !filters.assignedTo || t.assignedTo === filters.assignedTo,
        !filters.startDate ||
          (toDate(t.startDate)?.getTime() ?? 0) >= filters.startDate.getTime(),
        !filters.deadline ||
          (toDate(t.deadline)?.getTime() ?? 0) <= filters.deadline.getTime(),
      ];

      // ✅ Chip üzerinden gelen ek filtre
      const chipCheck = !labelFilter
        ? true
        : labelFilter.type === "label"
        ? (Array.isArray(t.labels) ? t.labels : [t.labels]).includes(
            labelFilter.value
          )
        : labelFilter.type === "priority"
        ? t.priority === labelFilter.value
        : labelFilter.type === "points"
        ? Number(t.points) === Number(labelFilter.value)
        : true;

      return baseChecks.every(Boolean) && chipCheck;
    });
  }, [useTasks, search, filters, labelFilter]);

  const isRowCheckable = (row) => row?.selectable !== false;

  // ---- columns ----
  const baseColumns = useMemo(
    () => [
      {
        id: "id",
        header: "ID",
        width: 110,
        accessor: "id",
        cell: (row, value) => (
          <div className={styles.idCell}>
            {isRowCheckable(row) ? (
              <input
                type="checkbox"
                className={styles.rowCheck}
                checked={row?.status === "Done"}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const next = e.target.checked ? "Done" : "To Do";
                  updateTasks((prev) =>
                    prev.map((t) =>
                      t.id === row.id ? { ...t, status: next } : t
                    )
                  );
                }}
                title="Mark as done"
              />
            ) : (
              <span className="task-check-placeholder" aria-hidden="true" />
            )}
            <span className="id-text">{value}</span>
          </div>
        ),
      },
      {
        id: "title",
        header: "Title",
        accessor: "title",
        cell: (row) => (
          <div title="View details" className={styles.titleCell}>
            <div>{row.title}</div>
            <div className={styles.titleChips}>
              {Array.isArray(row.labels) &&
                row.labels.map((lb, i) => (
                  <LabelChip
                    key={i}
                    color={labelColors?.[lb]}
                    active={
                      labelFilter?.type === "label" && labelFilter.value === lb
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFilter("label", lb);
                    }}
                  >
                    {lb}
                  </LabelChip>
                ))}
              <PriorityBadge
                level={row.priority}
                active={
                  labelFilter?.type === "priority" &&
                  labelFilter.value === row.priority
                }
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFilter("priority", row.priority);
                }}
              />
              <PointsPill
                value={row.points}
                active={
                  labelFilter?.type === "points" &&
                  Number(labelFilter.value) === Number(row.points)
                }
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFilter("points", row.points);
                }}
              />
            </div>
          </div>
        ),
      },
      { id: "start", header: "Start date", accessor: "startDate" },
      { id: "deadline", header: "Deadline", accessor: "deadline" },
      { id: "milestone", header: "Milestone", accessor: "milestone" },
      { id: "relatedTo", header: "Related to", accessor: "relatedTo" },
      {
        id: "assignedTo",
        header: "Assigned to",
        accessor: "assignedTo",
        cell: (row) => {
          const assignedUsers = resolveTaskUsers(
            row.assignedTo,
            row.assignedUserIds
          );

          return (
          <Link
            to={`${""}/app/users/profile/Dubai`}
            className={styles.assignedLink}
            onClick={(e) => e.stopPropagation()}
          >
            <AvatarStack
              users={
                assignedUsers.length
                  ? assignedUsers
                  : row.userImg
                  ? [
                      {
                        id: `fallback-${row.id}`,
                        name: row.userName || row.assignedTo || "User",
                        avatar: row.userImg,
                      },
                    ]
                  : []
              }
            />
          </Link>
        );
        },
      },
      {
        id: "collab",
        header: "Collaborators",
        accessor: "collaborators",
        cell: (row) => (
          <AvatarStack
            users={resolveTaskUsers(
              row.collaborators,
              row.collaboratorUserIds
            ).length
              ? resolveTaskUsers(row.collaborators, row.collaboratorUserIds)
              : row.userImg
              ? [
                  {
                    id: `fallback-collab-${row.id}`,
                    name: row.userName || "User",
                    avatar: row.userImg,
                  },
                ]
              : []}
          />
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: (row) => (
          <StatusCell
            task={row}
            setTasks={updateTasks}
            enableSearch={featureFlags.statusSearch}
          />
        ),
      },
      {
        id: "actions",
        header: <Menu color="#10161b" size={18} />,
        cell: (row) => (
          <div className={styles.rowActions}>
            <Button
              size="xs"
              color="light"
              className={styles.actionBtn}
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(row); // artık ListTask.jsx tarafında openEditModal çalışacak
              }}
            >
              <Edit size={16} color="#10161b " />
            </Button>

            <Button
              size="xs"
              color="light"
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) onDelete(row);
                else updateTasks((prev) => prev.filter((t) => t.id !== row.id));
              }}
              className={styles.actionBtn}
            >
              <X color="#10161b" size={18} />
            </Button>
          </div>
        ),
      },
    ],
    [featureFlags.statusSearch, onOpenInfo, updateTasks, labelFilter, labelColors]
  );

  const columns = useMemo(
    () => columnsProp || baseColumns,
    [columnsProp, baseColumns]
  );

  // ---- sorting ----
  const sorted = useMemo(() => {
    if (!sortBy) return filtered;
    const col = columns.find((c) => c.id === sortBy);
    if (!col) return filtered;

    const get = (row) =>
      typeof col.accessor === "function"
        ? col.accessor(row)
        : col.accessor
        ? row[col.accessor]
        : undefined;
    const keyFn = (v) => {
      if (v == null) return { t: "null", v: "" };
      const d = toDate(v);
      if (d instanceof Date && !isNaN(d)) return { t: "date", v: d.getTime() };
      const num = Number(v);
      if (!isNaN(num) && String(v).trim() !== "") return { t: "num", v: num };
      return { t: "str", v: String(v).toLowerCase() };
    };
    const cmp = (a, b) => {
      const ka = keyFn(get(a));
      const kb = keyFn(get(b));
      if (ka.t !== kb.t) return ka.t > kb.t ? 1 : -1;
      if (ka.t === "str") return ka.v.localeCompare(kb.v);
      return ka.v - kb.v;
    };
    const arr = [...filtered].sort(cmp);
    return sortDir === "asc" ? arr : arr.reverse();
  }, [filtered, sortBy, sortDir]);

  // ---- pagination ----
  const total = sorted.length;
  const paged = useMemo(() => {
    const start = (page - 1) * pageSizeState;
    return sorted.slice(start, start + pageSizeState);
  }, [sorted, page, pageSizeState]);
  const pages = Math.max(1, Math.ceil(total / pageSizeState));
  useEffect(() => {
    if (page > pages) setPage(pages);
    if (total === 0 && page !== 1) setPage(1);
  }, [page, pages, total]);

  const leftSlot =
    featureFlags.columnMenu || featureFlags.savedFilters ? (
      <div className={styles.toolbarSide}>
        {featureFlags.columnMenu && (
          <Dropdown
            isOpen={colMenuOpen}
            toggle={() => setColMenuOpen((v) => !v)}
          >
            <DropdownToggle color="white" className={styles.toolbarBtn}>
              <Columns2 size={16} />
            </DropdownToggle>
            <DropdownMenu className={styles.colsMenu}>
              {columns.map((col) => (
                <DropdownItem
                  key={col.id}
                  toggle={false}
                  onClick={() =>
                    setHiddenCols((prev) => {
                      const next = new Set(prev);
                      next.has(col.id) ? next.delete(col.id) : next.add(col.id);
                      return next;
                    })
                  }
                >
                  <input
                    type="checkbox"
                    readOnly
                    checked={!hiddenCols.has(col.id)}
                  />{" "}
                  {col.header || "(untitled)"}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        )}

        {featureFlags.savedFilters && (
          <>
            <Dropdown
              isOpen={quickFilterOpen}
              toggle={() => setQuickFilterOpen((prev) => !prev)}
            >
              {savedFilters.length > 0 ? (
                <DropdownToggle
                  color="white"
                  className={styles.toolbarBtn}
                  onClick={(event) => event.stopPropagation()}
                >
                  {activeSavedFilter
                    ? savedFilters.find((f) => f.id === activeSavedFilter)
                        ?.title
                    : "Quick filters"}
                  <ChevronDown size={14} />
                </DropdownToggle>
              ) : (
                ""
              )}

              <DropdownMenu className={styles.quickMenu}>
                {featureFlags.manageFilters && (
                  <DropdownItem
                    onClick={() => {
                      if (inspectorApi) openManageFiltersInspector();
                      else setManageFiltersOpen(true);
                    }}
                  >
                    Manage Filters
                  </DropdownItem>
                )}
                <DropdownItem
                  onClick={() => {
                    setFilters({
                      status: "",
                      project: "",
                      milestone: "",
                      priority: "",
                      label: "",
                      relatedTo: "",
                      assignedTo: "",
                      startDate: null,
                      deadline: null,
                    });
                    setActiveSavedFilter(null);
                  }}
                >
                  Clear
                </DropdownItem>
                <DropdownItem divider />
                {savedFilters.map((f) => (
                  <DropdownItem key={f.id} onClick={() => applySavedFilter(f)}>
                    {f.title}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            <div className={styles.savedFilterChips}>
              {savedFilters.map((f) => {
                const Icon = f.icon ? ICONS[f.icon] : null;
                return (
                  <Button
                    key={f.id}
                    size="sm"
                    color="white"
                    className={styles.toolbarBtn}
                    onClick={() => applySavedFilter(f)}
                  >
                    {f.bookmark && Icon ? <Icon size={16} /> : f.title}
                  </Button>
                );
              })}
            </div>
            <Button
              size="sm"
              color="white"
              className={`${styles.toolbarBtn} ${styles.addFilterBtn}`}
              onClick={() => setShowFilters((v) => !v)}
            >
              {savedFilters.length > 0 ? "+" : "+ Add new filter"}
            </Button>
          </>
        )}
      </div>
    ) : null;

  const rightSlot = (
    <div className={styles.rightSide}>
      {featureFlags.exportButtons && (
        <ExportButtons
          rows={sorted}
          columns={columns}
          fileName={(title || "tasks").toLowerCase().replace(/\s+/g, "_")}
          sheetName={title || "Tasks"}
          title={title || "Tasks"}
        />
      )}
      <div className={styles.searchWrap}>
        <Input
          type="search"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className={styles.searchInput}
        />
        <SearchIcon size={16} className={styles.searchIcon} />
      </div>
    </div>
  );

  return (
    <TableCard title={title} leftSlot={leftSlot} rightSlot={rightSlot}>
      {/* Seçili chip filtresi göstergesi */}
      {labelFilter && (
        <div className={styles.chipFilterWrap}>
          <span
            onClick={() => setLabelFilter(null)}
            className={styles.chipFilter}
            title="Clear chip filter"
          >
            {labelFilter.type}: {labelFilter.value} ×
          </span>
        </div>
      )}

      {featureFlags.filterBar && showFilters && (
        <div className={styles.filterRow}>
          {filterConfigs.map((cfg) => {
            const options = cfg.custom || createOptions(useTasks, cfg.key);
            return (
              <div className={styles.filterCol} key={cfg.key}>
                <Input
                  type="select"
                  className={styles.filterSelect}
                  value={filters[cfg.key]}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, [cfg.key]: e.target.value }));
                    setPage(1);
                  }}
                >
                  <option value="">{cfg.label}</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </div>
            );
          })}
          <div className={styles.filterCol}>
            <DatePicker
              selected={filters.startDate}
              onChange={(d) => setFilters((f) => ({ ...f, startDate: d }))}
              placeholderText="Start date (>=)"
              className={`form-control ${styles.filterSelect}`}
            />
          </div>
          <div className={styles.filterCol}>
            <DatePicker
              selected={filters.deadline}
              onChange={(d) => setFilters((f) => ({ ...f, deadline: d }))}
              placeholderText="Deadline (<=)"
              className={`form-control ${styles.filterSelect}`}
            />
          </div>
          <div className={styles.filterColActions}>
            {featureFlags.filterModal && (
              <Button
                size="xs"
                color="white"
                className={styles.resetBtn}
                onClick={toggleFilterModal}
                title="Clear dropdown filters"
              >
                <CheckCircle color="white" size={18} />
              </Button>
            )}
            <Button
              color="white"
              className={styles.closeFiltersBtn}
              size="xs"
              onClick={() => setShowFilters(false)}
              title="Close filters"
            >
              <XCircle color="#10161b" size={18} />
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={paged}
        hiddenColumnIds={[...hiddenCols]}
        activeRowId={activeTaskId}
        rowClassName={(row) =>
          String(row?.id) === String(activeTaskId) ? "task-table-row--active" : ""
        }
        onRowClick={onOpenInfo ? (row) => onOpenInfo(row) : undefined}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={(colId, dir) => {
          setSortBy(colId);
          setSortDir(dir);
        }}
      />
      <Pager
        page={page}
        pageSize={pageSizeState}
        total={total}
        onChange={setPage}
        onPageSizeChange={(size) => {
          setPageSizeState(size);
          setPage(1);
        }}
        pageSizes={[10, 25, 50, 100]}
      />
      {!inspectorApi && featureFlags.filterModal && (
        <FilterModal
          open={filterModalOpen}
          toggle={() => {
            setFilterModalOpen(false);
            setEditFilterData(null);
            setFilterMode("add");
          }}
          onSave={handleSaveFilter}
          mode={filterMode}
          initialData={editFilterData}
          icons={ICONS} // props olarak gönderiyoruz
        />
      )}

      {!inspectorApi && featureFlags.manageFilters && (
        <ManageFiltersModal
          open={manageFiltersOpen}
          toggle={() => setManageFiltersOpen(false)}
          filters={savedFilters}
          onApply={applySavedFilter}
          onEdit={editFilter}
          onDelete={deleteFilter}
        />
      )}
    </TableCard>
  );
}
